import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Moon, Menu, X, DollarSign, Book, HelpCircle, Crown, Zap, User } from 'lucide-react';
import { useUIStore } from '../../store';
import { useProAccess } from '../../hooks/useProAccess';
import { CreemPaymentButton } from '../payment/CreemPaymentButton';
import { cn } from '../../utils';
import type { ActiveSection } from '../../types';

interface NavigationItem {
  id: ActiveSection;
  label: string;
  icon: string;
  emoji: string;
}

const navigationItems: NavigationItem[] = [
  { id: 'calculator', label: 'Calculator', icon: 'Calculator', emoji: '🧮' },
  { id: 'scenarios', label: 'Scenarios', icon: 'Target', emoji: '🎯' },
  { id: 'timewheel', label: 'Moon Phase', icon: 'Moon', emoji: '🌙' },
  { id: 'ai', label: 'AI Assistant', icon: 'Bot', emoji: '🤖' },
  { id: 'pro', label: 'Pro', icon: 'Crown', emoji: '👑' }
];

interface PageNavigationItem {
  path: string;
  label: string;
  icon?: React.ReactNode;
}

const pageNavigationItems: PageNavigationItem[] = [
  { path: '/', label: 'Home' },
  { path: '/how-it-works', label: 'How It Works', icon: <HelpCircle className="h-4 w-4" /> },
  { path: '/sleep-guide', label: 'Sleep Guide', icon: <Book className="h-4 w-4" /> },
  { path: '/faq', label: 'FAQ', icon: <HelpCircle className="h-4 w-4" /> },
  { path: '/pricing', label: 'Pricing', icon: <DollarSign className="h-4 w-4" /> },
];

export const Header: React.FC = () => {
  const { activeSection, isMobileMenuOpen, setActiveSection, toggleMobileMenu } = useUIStore();
  const { currentPlan, isProUser, isPremiumUser, isLoading } = useProAccess();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const handleSectionClick = (sectionId: ActiveSection) => {
    setActiveSection(sectionId);
  };

  const buildCreemUrl = (prodId: string) => {
    const uid = (() => {
      try { return localStorage.getItem('sleepcalc_user_id') || 'guest'; } catch { return 'guest'; }
    })();
    return `https://www.creem.io/payment/${prodId}?customer_id=${encodeURIComponent(uid)}&success_url=${encodeURIComponent('https://sleepcalc-pro.pages.dev/?payment=success')}&cancel_url=${encodeURIComponent('https://sleepcalc-pro.pages.dev/?payment=cancel')}`;
  };

  return (
    <header className="sticky top-0 z-[2000] w-full border-b border-slate-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:border-slate-800 dark:bg-slate-950/95 dark:supports-[backdrop-filter]:bg-slate-950/60 relative">
      <div className="container mx-auto px-4 relative">
        <div className="flex h-16 items-center justify-between">
          {/* Brand */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-white">
              <Moon className="h-5 w-5" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                SleepCalc Pro
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                v3.0 - Professional Sleep Optimization
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            <nav className="flex items-center space-x-1">
              {isHomePage ? (
                // Show section navigation on home page
                navigationItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleSectionClick(item.id)}
                    className={cn(
                      'flex items-center space-x-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      'hover:bg-slate-100 dark:hover:bg-slate-800',
                      activeSection === item.id
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                        : 'text-slate-600 dark:text-slate-300'
                    )}
                  >
                    <span className="text-base">{item.emoji}</span>
                    <span>{item.label}</span>
                  </button>
                ))
              ) : (
                // Show page navigation on other pages
                pageNavigationItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      'flex items-center space-x-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      'hover:bg-slate-100 dark:hover:bg-slate-800',
                      location.pathname === item.path
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                        : 'text-slate-600 dark:text-slate-300'
                    )}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </Link>
                ))
              )}
            </nav>

            {/* User Status & Upgrade Button */}
            <div className="flex items-center space-x-2">
              {!isLoading && (
                <>
                  {/* User Plan Badge */}
                  <div className={cn(
                    'flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium',
                    isPremiumUser
                      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400'
                      : isProUser
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                      : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                  )}>
                    {isPremiumUser ? <Crown className="w-3 h-3" /> : isProUser ? <Zap className="w-3 h-3" /> : <User className="w-3 h-3" />}
                    <span>{currentPlan === 'free' ? 'Free' : currentPlan === 'pro' ? 'Pro' : 'Premium'}</span>
                  </div>

                  {/* Upgrade Button for Free Users */}
                  {currentPlan === 'free' && (
                    <div className="relative z-[2500] isolate pointer-events-auto">
                      <CreemPaymentButton 
                        plan="pro" 
                        variant="primary"
                        size="sm"
                        className="!px-3 !py-1.5 !text-sm"
                      />
                    </div>
                  )}
                  
                  {/* Upgrade to Premium for Pro Users */}
                  {currentPlan === 'pro' && (
                    <div className="relative z-[2500] isolate pointer-events-auto">
                      <CreemPaymentButton 
                        plan="premium" 
                        variant="outline"
                        size="sm"
                        className="!px-3 !py-1.5 !text-sm"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="md:hidden rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            aria-label="Toggle navigation menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Absolute overlay CTA to bypass any stray overlays; only in production */}
        {!isLoading && import.meta.env.PROD && (
          <>
            {currentPlan === 'free' && (
              <a
                href={buildCreemUrl('prod_10w6tRy1qlvUVAkyiDgX7E')}
                target="_blank"
                rel="noreferrer noopener"
                aria-label="Upgrade to SleepCalc Pro"
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-blue-600 to-cyan-600 shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 pointer-events-auto"
                style={{ position: 'fixed', right: '1rem', top: '0.5rem', zIndex: 100000 }}
                data-role="upgrade-cta"
              >
                Upgrade to SleepCalc Pro
              </a>
            )}
            {currentPlan === 'pro' && (
              <a
                href={buildCreemUrl('prod_2mKSW1peZ6P7A1MpZNlcdc')}
                target="_blank"
                rel="noreferrer noopener"
                aria-label="Upgrade to SleepCalc Premium"
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold rounded-lg text-purple-50 bg-gradient-to-r from-purple-600 to-indigo-600 shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-purple-500 pointer-events-auto"
                style={{ position: 'fixed', right: '1rem', top: '0.5rem', zIndex: 100000 }}
                data-role="upgrade-cta"
              >
                Upgrade to Premium
              </a>
            )}
          </>
        )}

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 dark:border-slate-800">
            <nav className="py-4">
              {isHomePage ? (
                // Show section navigation on home page
                <div className="grid grid-cols-2 gap-2">
                  {navigationItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleSectionClick(item.id)}
                      className={cn(
                        'flex items-center space-x-3 rounded-lg p-3 text-sm font-medium transition-colors',
                        'hover:bg-slate-100 dark:hover:bg-slate-800',
                        activeSection === item.id
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                          : 'text-slate-600 dark:text-slate-300'
                      )}
                    >
                      <span className="text-lg">{item.emoji}</span>
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              ) : (
                // Show page navigation on other pages
                <div className="space-y-2">
                  {pageNavigationItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={cn(
                        'flex items-center space-x-3 rounded-lg p-3 text-sm font-medium transition-colors',
                        'hover:bg-slate-100 dark:hover:bg-slate-800',
                        location.pathname === item.path
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                          : 'text-slate-600 dark:text-slate-300'
                      )}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
